import { useState } from "react";
import { cn } from "@/utils/cn";
import { exportData } from "@/utils/export";
import { exportSummaryPdf, printSummaryPdf } from "@/utils/pdfDocument";
import type { ReportResult } from "./types";
import { buildExportColumns, buildExportRows } from "./utils";

interface ReportActionsProps {
  result: ReportResult;
  filterLines: { label: string; value: string }[];
  filename: string;
}

type BusyKey = "xlsx" | "csv" | "pdf" | "print" | null;

export default function ReportActions({ result, filterLines, filename }: ReportActionsProps) {
  const [busy, setBusy] = useState<BusyKey>(null);
  const [error, setError] = useState<string | null>(null);

  const isEmpty = result.rows.length === 0 || result.columns.length === 0;

  const buildPdfSections = () => [
    {
      heading: "Filtres appliqués",
      fields: filterLines.map((line) => ({ label: line.label, value: line.value })),
    },
    ...(result.totals.length > 0
      ? [
          {
            heading: "Totaux",
            fields: result.totals.map((total) => ({ label: total.label, value: total.value })),
          },
        ]
      : []),
    {
      heading: "Détail",
      table: {
        columns: result.columns.map((column) => column.header),
        rows: result.rows.map((row) => result.columns.map((column) => String(row[column.key] ?? ""))),
      },
    },
  ];

  const run = async (key: Exclude<BusyKey, null>, action: () => Promise<void>) => {
    if (isEmpty || busy) return;
    setError(null);
    setBusy(key);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "L'opération a échoué.");
    } finally {
      setBusy(null);
    }
  };

  const handleTabular = (format: "xlsx" | "csv") =>
    run(format, () =>
      exportData(format, buildExportRows(result), buildExportColumns(result), {
        filename,
        title: result.title,
        subtitle: filterLines.map((line) => `${line.label} : ${line.value}`).join("  ·  "),
      })
    );

  const handlePdf = () =>
    run("pdf", () => exportSummaryPdf({ filename, title: result.title, sections: buildPdfSections() }));

  const handlePrint = () =>
    run("print", () => printSummaryPdf({ filename, title: result.title, sections: buildPdfSections() }));

  const buttons: { key: Exclude<BusyKey, null>; label: string; icon: string; onClick: () => void }[] = [
    { key: "xlsx", label: "Télécharger Excel", icon: "ri-file-excel-2-line", onClick: () => handleTabular("xlsx") },
    { key: "csv", label: "Télécharger CSV", icon: "ri-file-text-line", onClick: () => handleTabular("csv") },
    { key: "pdf", label: "Télécharger PDF", icon: "ri-file-pdf-2-line", onClick: handlePdf },
    { key: "print", label: "Imprimer", icon: "ri-printer-line", onClick: handlePrint },
  ];

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex flex-col gap-1">
        <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
          Exporter le rapport affiché
        </h3>
        <p className="text-xs text-foreground-500">
          Les fichiers reprennent exactement les filtres appliqués et les données affichées ci-dessus.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {buttons.map((button) => (
          <button
            key={button.key}
            type="button"
            onClick={button.onClick}
            disabled={isEmpty || Boolean(busy)}
            className={cn(
              "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-label font-medium transition-colors",
              isEmpty || Boolean(busy)
                ? "cursor-not-allowed border-background-200 bg-background-100 text-foreground-400"
                : "border-foreground-200 bg-background-50 text-foreground-900 hover:bg-background-100 cursor-pointer"
            )}
          >
            <i
              className={cn(busy === button.key ? "ri-loader-4-line animate-spin" : button.icon, "text-base")}
              aria-hidden="true"
            />
            {button.label}
          </button>
        ))}
      </div>

      {isEmpty && (
        <p className="text-xs text-foreground-400">
          Aucune donnée à exporter : ajustez les filtres puis régénérez le rapport.
        </p>
      )}

      {error && (
        <p className="flex items-center gap-1.5 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-800">
          <i className="ri-error-warning-line text-sm" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}